import { axiosInstance } from 'utils/AxiosInstance'
import { API_PATHS } from 'utils/constants'

export interface InspectionAssignmentDTO {
  _id?: string
  id?: string
  revision?: any
  organization?: any
  inspector?: any
  targetOrganization?: any
  hasUserChecks?: boolean
  hasInspectorAssignment?: boolean
  userStatus?: 'not_started' | 'in_progress' | 'completed'
  inspectorStatus?: 'awaiting' | 'pending' | 'reviewed' | 'checked'
  // Legacy field for backward compatibility
  status?: 'awaiting' | 'reviewed' | 'approved' | 'returned' | 'checked' | 'pending'
  note?: string
  updatedAt?: string
  createdAt?: string
}

export const getMyInspections = async (status?: string) => {
  const url = status ? `${API_PATHS.INSPECTIONS_MY}?status=${encodeURIComponent(status)}` : API_PATHS.INSPECTIONS_MY
  const { data } = await axiosInstance.get<{ data: InspectionAssignmentDTO[] }>(url)
  return data.data
}

export const getInspectionDashboard = async (status?: string, organizationId?: string) => {
  let url = '/inspections/dashboard'
  const params = new URLSearchParams()
  
  if (status) {
    params.append('status', status)
  }
  if (organizationId) {
    params.append('organizationId', organizationId)
  }
  
  if (params.toString()) {
    url += `?${params.toString()}`
  }
  
  const { data } = await axiosInstance.get<{ data: InspectionAssignmentDTO[], role: string, totalCount: number }>(url)
  return data
}

export interface AdminInspectionsListResponse {
  data: InspectionAssignmentDTO[]
  totalCount: number
  paging?: { total: number; page: number; pageSize: number }
}

/** Organization that has at least one inspection assignment (admin filter dropdown) */
export interface InspectionFilterOrgOption {
  id: string
  company_name: string
  image_url?: string
}

export const getAdminInspectionFilterOrganizations = async (params: {
  type: 'source' | 'target'
  search?: string
  limit?: number
}): Promise<InspectionFilterOrgOption[]> => {
  const searchParams = new URLSearchParams()
  searchParams.append('type', params.type)
  if (params.search?.trim()) searchParams.append('search', params.search.trim())
  if (params.limit != null) searchParams.append('limit', String(params.limit))
  const query = searchParams.toString()
  const url = `/admin/inspections/filter-organizations?${query}`
  const { data } = await axiosInstance.get<{ data: InspectionFilterOrgOption[] }>(url)
  return data.data ?? []
}

export const getAdminInspectionsList = async (params?: {
  status?: string
  page?: number
  pageSize?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  search?: string
  /** Inspection source / lab organization (assignment.targetOrganization) */
  sourceOrgId?: string
  /** Client business organization (assignment.organization) */
  targetOrgId?: string
}) => {
  const searchParams = new URLSearchParams()
  if (params?.status) searchParams.append('status', params.status)
  if (params?.page != null) searchParams.append('page', String(params.page))
  if (params?.pageSize != null) searchParams.append('pageSize', String(params.pageSize))
  if (params?.sortBy) searchParams.append('sortBy', params.sortBy)
  if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder)
  if (params?.search?.trim()) searchParams.append('search', params.search.trim())
  if (params?.sourceOrgId) searchParams.append('sourceOrgId', params.sourceOrgId)
  if (params?.targetOrgId) searchParams.append('targetOrgId', params.targetOrgId)

  const query = searchParams.toString()
  const url = query ? `/admin/inspections?${query}` : '/admin/inspections'
  const { data } = await axiosInstance.get<AdminInspectionsListResponse>(url)
  return data
}

export interface MyChecksDashboardResponse {
  data: InspectionAssignmentDTO[]
  totalCount: number
  paging?: { total: number; page: number; pageSize: number }
}

export interface MyChecksStats {
  not_started: number
  pending: number
  checked: number
}

export const getUserInspectionDashboard = async (params?: {
  status?: string
  page?: number
  pageSize?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  search?: string
}) => {
  const searchParams = new URLSearchParams()
  if (params?.status) searchParams.append('status', params.status)
  if (params?.page != null) searchParams.append('page', String(params.page))
  if (params?.pageSize != null) searchParams.append('pageSize', String(params.pageSize))
  if (params?.sortBy) searchParams.append('sortBy', params.sortBy)
  if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder)
  if (params?.search?.trim()) searchParams.append('search', params.search.trim())

  const query = searchParams.toString()
  const url = query ? `/inspections/my-checks?${query}` : '/inspections/my-checks'
  const { data } = await axiosInstance.get<MyChecksDashboardResponse>(url)
  return data
}

export const getUserInspectionStats = async (): Promise<MyChecksStats> => {
  const { data } = await axiosInstance.get<MyChecksStats>('/inspections/my-checks/stats')
  return data
}

export const updateInspectionStatus = async (assignmentId: string, status: any, note?: string) => {
  const { data } = await axiosInstance.patch(`${API_PATHS.INSPECTIONS}/${assignmentId}/status`, { status, note })
  return data
}

export const markInspectionViewed = async (assignmentId: string) => {
  const { data } = await axiosInstance.patch(`${API_PATHS.INSPECTIONS}/${assignmentId}/view`, {})
  return data
}

export const assignInspectorToRevision = async (
  revisionId: string, 
  projectId: string, 
  contextOrganizationId: string, 
  assignment: { 
    email?: string, 
    targetOrganizationId?: string, 
    assignmentType: 'inspector' | 'organization' 
  }
) => {
  const { data } = await axiosInstance.post(
    `${API_PATHS.REVISIONS}/${revisionId}/inspections/assign`, 
      assignment,
    {
      headers: {    
        'x-project-id': projectId,
        'x-organization-id': contextOrganizationId
    }
  }
  )
  return data
}

// New interfaces for inspection organizations
export interface InspectionOrganizationDTO {
  id: string
  company_name: string
  company_email: string
  image_url?: string
}

export interface InspectorDTO {
  id: string
  name: string
  email: string
}

// Get all inspection type organizations
export const getInspectionOrganizations = async (): Promise<InspectionOrganizationDTO[]> => {
  const { data } = await axiosInstance.get<InspectionOrganizationDTO[]>(API_PATHS.INSPECTION_ORGANIZATIONS)
  return data
}

// Get inspectors from a specific inspection organization
export const getInspectorsFromOrganization = async (organizationId: string): Promise<InspectorDTO[]> => {
  const { data } = await axiosInstance.get<InspectorDTO[]>(`/organizations/${organizationId}/inspectors`)
  return data
}

// Download validation report PDF for a revision
// fromReportPage: when true, always shows verification points and code values (everyone can see them)
// includeAllInputs: when true, each verification point lists every compared input, not only differences
export const downloadValidationReport = async (
  revisionId: string,
  fromReportPage: boolean = false,
  includeAllInputs: boolean = false
) => {
  const params = new URLSearchParams()
  if (fromReportPage) params.set('fromReportPage', 'true')
  if (includeAllInputs) params.set('includeAllInputs', 'true')
  const query = params.toString()
  const url = `/inspections/${revisionId}/report${query ? `?${query}` : ''}`
  
  const response = await axiosInstance.get(url, {
    responseType: 'blob'
  })
  
  // Create blob and download
  const blob = new Blob([response.data], { type: 'application/pdf' })
  const blobUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = includeAllInputs
    ? `validation_report_${revisionId}_all_inputs_${Date.now()}.pdf`
    : `validation_report_${revisionId}_${Date.now()}.pdf`
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(blobUrl)
}

// Verification data interfaces
export interface VerificationData {
  inspectorPointStatuses?: Record<string, 'unverified' | 'pass' | 'fail'>
  inspectorPointComments?: Record<string, string>
  /** JPEG data URLs per checklist point — embedded in the validation PDF. */
  inspectorPointShots?: Record<string, string[]>
  validationForm: {
    assessorName?: string
    signature?: string
    date?: string
    finalComment: string
    isApproved: boolean
  }
}

// Save verification data for a revision
export const saveVerificationData = async (revisionId: string, verificationData: VerificationData) => {
  const { data } = await axiosInstance.patch(`/inspections/${revisionId}/verification-data`, { verificationData })
  return data
}

// Response interface for getVerificationData
export interface VerificationDataResponse {
  verificationData: VerificationData
  userStatus?: 'not_started' | 'in_progress' | 'completed'
  inspectorStatus?: 'awaiting' | 'pending' | 'reviewed' | 'checked'
}

// Get verification data for a revision
export const getVerificationData = async (revisionId: string): Promise<VerificationDataResponse> => {
  const { data } = await axiosInstance.get<VerificationDataResponse>(`/inspections/${revisionId}/verification-data`)
  return data
}

// Generate shareable report link
export const generateShareableReportLink = async (
  revisionId: string
): Promise<{ url: string; shareCode: string }> => {
  const { data } = await axiosInstance.post<{ url: string; shareCode: string }>(
    `/inspections/${revisionId}/share-link`,
    {}
  )
  return data
}

export const logModIdfDownload = async (
  code: string,
  consentAcknowledged: boolean
): Promise<{ downloadUrl: string }> => {
  const { data } = await axiosInstance.post<{ downloadUrl: string }>(
    '/inspection/mod-idf-download',
    { code, consentAcknowledged }
  )
  return data
}

// Check inspection access before allowing access to checklist page
export const checkInspectionAccess = async (
  revisionId: string,
  shareCode?: string
): Promise<{ hasAccess: boolean }> => {
  const url = shareCode 
    ? `/inspections/${revisionId}/check-access?shareCode=${shareCode}`
    : `/inspections/${revisionId}/check-access`
  const { data } = await axiosInstance.get<{ hasAccess: boolean }>(url)
  return data
}


